import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, Signal, signal, untracked, ViewChild, WritableSignal, OnInit, OnDestroy, AfterViewInit, ElementRef, ChangeDetectorRef } from '@angular/core';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { AttachmentType, CollaboraterRole, EntityName, EntityStatus, ShowType } from '@app/_types/generic.type';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { CacheService } from '@app/_services/cache.service';
import { SettingsService } from '@app/_services/settings.service';
import { BRAND_LOGO } from '@app/_utils/brand-icons';
import { NotificationComponent } from '@app/components/common/notifications/notification/notification.component';
import { NoResultBlockComponent } from '@app/components/shared/no-result-block/no-result-block.component';
import { AllFeaturesDisabledBlockComponent } from '@app/components/shared/all-features-disabled-block/all-features-disabled-block.component';
import { DragScrollDirective } from '@app/_directives/drag-scroll.directive';
import { FilterSearchComponent } from '@app/components/shared/filters/filter-search/filter-search.component';
import { FilterHashtagComponent } from '@app/components/shared/filters/filter-hashtag/filter-hashtag.component';
import { FilterAttachmentComponent } from '@app/components/shared/filters/filter-attachment/filter-attachment.component';
import { FilterStatusComponent } from '@app/components/shared/filters/filter-status/filter-status.component';
import { FilterMoodComponent } from '@app/components/shared/filters/filter-mood/filter-mood.component';
import { Entry, FlatEntry } from '@app/_models/entry.model';
import { TodoBlockComponent } from '../../todos/todo-block/todo-block.component';
import { CalendarEventBlockComponent } from '../../calendar-integration/calendar-event-block/calendar-event-block.component';
import { HabitBlockComponent } from '../../habits/habit-block/habit-block.component';
import { JournalBlockComponent } from '../../journals/journal-block/journal-block.component';
import { MoneyTrackerBlockComponent } from '../../money-tracker/money-tracker-block/money-tracker-block.component';
import { AnalyticsService } from '@app/_services/analytics.service';
import { Subject, take, takeUntil } from 'rxjs';
import { IndexDbService } from '@app/_services/index-db.service';
import { LoaderComponent } from '@app/components/shared/loader/loader.component';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { FeatureService } from '@app/_services/features/features.service';
import { FilterFeatureComponent } from '@app/components/shared/filters/filter-feature/filter-feature.component';
import { FilterRoleComponent } from '@app/components/shared/filters/filter-role/filter-role.component';
import { Attachment, MembersModel } from '@app/_interfaces/generic.interface';
import { FilterDateRangeComponent } from '@app/components/shared/filters/filter-date-range/filter-date-range.component';
import { AlertService } from '@app/_services/alert.service';
import { JournalService } from '@app/_services/features/journal.service';
import { TodoService } from '@app/_services/features/todo.service';
import { HabitService } from '@app/_services/features/habit.service';
import { MobileBottomBarComponent } from '@app/components/shared/mobile-bottom-bar/mobile-bottom-bar.component';
import { FeatureStore, ViewSettingsStore, JournalStore, NoteStore } from '@app/_stores';
import { EntityPastFutureViewComponent } from '../entity-past-future-view/entity-past-future-view.component';
import { ModalService } from '@app/_services/modal.service';
import { FocusBlockComponent } from '../../focus-tracker/focus-block/focus-block.component';
import { NoteBlockComponent } from '../../notes/note-block/note-block.component';
import { log } from '@app/_services/logger';
import { DateCursor, DatePaginationService, EntityMetadata, PaginationFilters } from '@app/_services/pagination/date-pagination.service';
import { PastFuturePrefetchService } from '@app/_services/past-future-prefetch.service';
import { EntityDateIndex, EntityDateIndexService } from '@app/_services/entity-date-index.service';
import { getRoleByUid } from '@app/_utils/members-model.utils';
import { MoneyTrackerStore, FocusStore } from '@app/_stores';
import { PAST_FUTURE_PAGINATION } from '@app/_configs/pagination.config';

@Component({
  selector: 'app-entity-past-and-future',
  imports: [
    CommonModule,
    MatMenuModule,
    SvgComponent,
    NotificationComponent,
    DragScrollDirective,
    NoResultBlockComponent,
    FilterSearchComponent,
    FilterFeatureComponent,
    FilterRoleComponent,
    FilterHashtagComponent,
    FilterAttachmentComponent,
    FilterStatusComponent,
    FilterDateRangeComponent,
    TodoBlockComponent,
    HabitBlockComponent,
    JournalBlockComponent,
    MoneyTrackerBlockComponent,
    CalendarEventBlockComponent,
    FocusBlockComponent,
    NoteBlockComponent,
    MobileBottomBarComponent,
    AllFeaturesDisabledBlockComponent,
    LoaderComponent,
    FilterMoodComponent
  ],
  templateUrl: './entity-past-and-future.component.html',
  styleUrl: './entity-past-and-future.component.scss'
})

export class EntityPastAndFutureComponent implements OnInit, AfterViewInit, OnDestroy {

  readonly brandLogo = BRAND_LOGO;
  @ViewChild('scrollContainer', { read: ElementRef }) scrollContainer!: ElementRef<HTMLElement>;
  @ViewChild('loadMoreSentinel', { read: ElementRef }) loadMoreSentinel?: ElementRef<HTMLElement>;

  // IntersectionObserver for lazy loading
  private loadMoreObserver?: IntersectionObserver;

  // Flag to prevent loading during collapse/expand operations
  private isAdjustingView = false;

  // Last load timestamp to prevent rapid consecutive loads
  private lastLoadTime = 0;

  // Inject services
  private readonly paginationService = inject(DatePaginationService);
  private readonly prefetchService = inject(PastFuturePrefetchService);
  private readonly indexService = inject(EntityDateIndexService);
  private readonly indexDbService = inject(IndexDbService);
  readonly featureStore = inject(FeatureStore);  // Used in template
  private readonly viewSettings = inject(ViewSettingsStore);
  private readonly journalStore = inject(JournalStore);
  private readonly noteStore = inject(NoteStore);
  private readonly moneyTrackerStore = inject(MoneyTrackerStore);
  private readonly focusStore = inject(FocusStore);

  // Pre-built index for FUTURE mode smart pagination (reused across pages, cleared on reset)
  private futureIndex: EntityDateIndex | null = null;

  // Timer for debouncing silent refreshes triggered by store changes
  private silentRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  // Component state
  mode = signal<'PAST' | 'FUTURE'>('PAST');
  screenEnterTime: Signal<Date> = signal(new Date());
  unSubscribe = new Subject<void>();

  // Date range constraints for filter component
  minDate: Signal<Date> = computed(() => {
    const mode = this.mode();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return mode === 'PAST' ? new Date('2000-01-01') : tomorrow;
  });

  maxDate: Signal<Date> = computed(() => {
    const mode = this.mode();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return mode === 'PAST' ? yesterday : new Date('2100-01-01');
  });

  // Loading states
  loading = signal(false);  // Start false; set true only if cache miss
  loadingMore = signal(false);

  // Reload lock to prevent multiple simultaneous reloads
  private isReloading = signal(false);

  // Track if initial load has completed
  private hasLoadedOnce = signal(false);

  // Filter state
  isFilter: WritableSignal<boolean> = signal(false);
  filters: {
    query: WritableSignal<string>;
    tags: WritableSignal<string[]>;
    attachments: WritableSignal<AttachmentType[]>;
    moods: WritableSignal<number[]>;
    status: WritableSignal<EntityStatus[]>;
    feature: WritableSignal<EntityName[]>;
    role: WritableSignal<CollaboraterRole[]>;
    startDate: WritableSignal<Date | null>;
    endDate: WritableSignal<Date | null>;
  } = {
      query: signal<string>(''),
      tags: signal<string[]>([]),
      attachments: signal<AttachmentType[]>([]),
      moods: signal<number[]>([]),
      status: signal<EntityStatus[]>([]),
      feature: signal<EntityName[]>([]),
      role: signal<CollaboraterRole[]>([]),
      startDate: signal<Date | null>(null),
      endDate: signal<Date | null>(null),
    };

  // Check if Focus is selected (used to hide certain filter options)
  isFocusSelected: Signal<boolean> = computed(() => {
    const features = this.filters.feature();
    return features.includes('focus');
  });

  // Check if there are journal/note entities with mood data in past or future based on mode
  hasJournalDataInTimeRange: Signal<boolean> = computed(() => {
    const mode = this.mode();
    const activeEntities = this.featureStore.activeEntities();

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayDateString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // Check notes with mood data in time range (notes have emotion field)
    if (activeEntities.includes('note')) {
      const notesByDate = this.noteStore.notesByDateString();
      for (const dateString in notesByDate) {
        if (mode === 'PAST' && dateString < todayDateString) {
          return true;
        } else if (mode === 'FUTURE' && dateString >= todayDateString) {
          return true;
        }
      }
    }

    // Check journal data in time range
    if (activeEntities.includes('journal')) {
      // First check: Do we have journal entries in the time range?
      const journalMap = this.journalStore.setupToJournalMap();
      for (const setupId in journalMap) {
        const dateMap = journalMap[setupId];
        for (const dateString in dateMap) {
          const journal = dateMap[dateString];
          if (journal && !journal.deletedAt) {
            if (mode === 'PAST' && dateString < todayDateString) {
              return true;
            } else if (mode === 'FUTURE' && dateString >= todayDateString) {
              return true;
            }
          }
        }
      }

      // Second check: Do we have journal setups that cover the time range?
      const journalSetups = this.journalStore.enabledSetups();
      for (const setup of journalSetups) {
        if (!setup.startDate) continue;

        const startDate = setup.startDate;
        const endDate = setup.endDate || '9999-12-31';

        if (mode === 'PAST') {
          if (startDate < todayDateString) {
            return true;
          }
        } else if (mode === 'FUTURE') {
          if (endDate >= todayDateString) {
            return true;
          }
        }
      }
    }

    return false;
  });

  // Pagination state
  private cursor = signal<DateCursor | null>(null);
  hasMore = signal(true);  // Public - used in template

  // Data storage - flattened items from pagination
  flatenedData = signal<FlatEntry[]>([]);

  // Computed data for template compatibility (grouped by date)
  computedData: Signal<Record<string, Entry[]>> = computed(() => {
    const items = this.flatenedData();
    const grouped: Record<string, Entry[]> = {};

    items.forEach(item => {
      if (item.type === 'item' && item.entry) {
        if (!grouped[item.dateString]) {
          grouped[item.dateString] = [];
        }
        grouped[item.dateString].push(item.entry);
      }
    });
    return grouped;
  });

  /**
   * Returns true only if data has loaded AND there are no entities.
   */
  isEmpty: Signal<boolean> = computed(() => {
    if (this.loading()) return false;
    return this.flatenedData().filter(item => item.type === 'item').length === 0;
  });

  isFiltered: Signal<boolean> = computed(() => {
    return this.filters.query() !== '' ||
      this.filters.feature().length > 0 ||
      this.filters.role().length > 0 ||
      this.filters.tags().length > 0 ||
      this.filters.attachments().length > 0 ||
      this.filters.moods().length > 0 ||
      this.filters.status().length > 0 ||
      this.filters.startDate() !== null ||
      this.filters.endDate() !== null;
  });

  config: Signal<{ [key: string]: { name: string } }> = computed(() => ({
    PAST: {
      name: this.ss.t('navigationBar_past')
    },
    FUTURE: {
      name: this.ss.t('navigationBar_future')
    }
  }));

  showConfig: Signal<ShowType[]> = computed(() => {
    const mode = this.mode();
    return mode === 'PAST' ? this.viewSettings.pastShow() : this.viewSettings.futureShow();
  });

  // Stable string key for showConfig — changes only when CONTENT changes, not array reference.
  // ViewSettingsStore.patchState() creates new arrays on every viewSettings$ emit (even same data),
  // which would otherwise cause the filter effect to call resetAndReload() spuriously.
  private showConfigKey: Signal<string> = computed(() => [...this.showConfig()].sort().join(','));

  descriptionType: Signal<string> = computed(() => {
    // Always use 'short' when description is in show array, 'none' otherwise
    const show = this.showConfig();
    return show.includes('description') ? 'short' : 'none';
  });

  // Collapsed view setting - if true, groups default to collapsed
  // Note: Uses ss.viewSignals() to stay in sync with the dropdown settings
  collapsedView: Signal<boolean> = computed(() => {
    const mode = this.mode();
    const viewSignals = this.ss.viewSignals();
    const groupedViewType = mode === 'PAST'
      ? viewSignals['pastTodoGroupedViewType']()
      : viewSignals['futureTodoGroupedViewType']();
    return groupedViewType.includes('collapsedView');
  });

  // Show counts setting - if true, show item count next to group headers
  // Note: Uses ss.viewSignals() to stay in sync with the dropdown settings
  showCounts: Signal<boolean> = computed(() => {
    const mode = this.mode();
    const viewSignals = this.ss.viewSignals();
    const groupedViewType = mode === 'PAST'
      ? viewSignals['pastTodoGroupedViewType']()
      : viewSignals['futureTodoGroupedViewType']();
    return groupedViewType.includes('showCounts');
  });

  // Group by setting - 'date' or 'hashtag' (hashtag only for Past)
  // Note: Uses ss.viewSignals() to stay in sync with the dropdown which also uses SettingsService
  groupBy: Signal<'date' | 'hashtag'> = computed(() => {
    const mode = this.mode();
    const viewSignals = this.ss.viewSignals();
    return (mode === 'PAST' ? viewSignals['pastTodoGroupBy']() : viewSignals['futureTodoGroupBy']()) as 'date' | 'hashtag';
  });

  private readonly PAGE_SIZE = PAST_FUTURE_PAGINATION.LIST_PAGE_SIZE;
  private readonly MAX_DATES_TO_SCAN = 60;
  currentPage = signal(1);

  // Sticky header state (fixed overlay at top)
  currentStickyHeader = signal<FlatEntry | null>(null);

  // Group expansion state (for expand/collapse functionality)
  private expandedGroups = signal<Set<string>>(new Set());

  constructor(
    private router: Router,
    public cc: CacheService,
    public ss: SettingsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private featureService: FeatureService,
    private alertService: AlertService,
    private habitService: HabitService,
    private todoService: TodoService,
    private journalService: JournalService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef,
  ) {
    const mode = this.router.url.includes('past') ? 'PAST' : 'FUTURE';
    this.mode.set(mode);

    // Effect to reload when filters, groupBy, or showInvalidEntries change
    let filterEffectInitialized = false;

    effect(() => {
      // Read all filter signals to track them (underscore prefix indicates intentional unused)
      const _query = this.filters.query();
      const _features = this.filters.feature();
      const _access = this.filters.role();
      const _tags = this.filters.tags();
      const _status = this.filters.status();
      const _attachments = this.filters.attachments();
      const _moods = this.filters.moods();
      const _startDate = this.filters.startDate();
      const _endDate = this.filters.endDate();
      const _groupBy = this.groupBy();

      // Track invalid entry setting via stable key (primitive string — immune to reference churn
      // from ViewSettingsStore.patchState creating new arrays on every viewSettings$ emit).
      const _showConfigKey = this.showConfigKey();

      // Trigger reload (untracked to avoid infinite loop)
      untracked(() => {
        // Skip the initialization run — ngOnInit handles the first load (with cache support).
        // Subsequent runs are real filter changes and must trigger a reload.
        if (!filterEffectInitialized) {
          filterEffectInitialized = true;
          return;
        }

        // Skip reload if currently loading/reloading (prevents interruption)
        const isCurrentlyLoading = this.loading() || this.loadingMore() || this.isReloading();

        if (isCurrentlyLoading) {
          return;
        }

        this.resetAndReload();
      });
    });

    // Effect to re-setup observer when hasMore or loading state changes.
    // flatenedData and loadingMore are intentionally excluded: appending pages
    // does not move the sentinel element, so the existing observer remains valid.
    // setupIntersectionObserver() already guards against double-loading via
    // !this.loadingMore() and this.hasMore() inside the callback.
    effect(() => {
      const _hasMore = this.hasMore();
      const _loading = this.loading();

      untracked(() => {
        requestAnimationFrame(() => {
          if (this.loadMoreSentinel?.nativeElement) {
            this.setupIntersectionObserver();
          }
        });
      });
    });

    // Effect to prevent loading during collapse/expand operations
    let collapseEffectInitialized = false;

    effect(() => {
      const _collapsedView = this.collapsedView();

      untracked(() => {
        if (!collapseEffectInitialized) {
          collapseEffectInitialized = true;
          return;  // skip init run — not a real user action
        }

        // Real collapse/expand click — block loading briefly to let DOM settle
        this.isAdjustingView = true;
        setTimeout(() => {
          this.isAdjustingView = false;
        }, 500);
      });
    });

    // Effect to silently refresh rendered dates when store data changes (e.g. mobile sync)
    this.setupStoreWatchEffect();

  }

  ngOnInit() {
    this.loadInitialPage();
    this.analyticsInit();
  }

  ngAfterViewInit() {
    // Setup IntersectionObserver after view is initialized
    setTimeout(() => this.setupIntersectionObserver(), 100);
  }

  /**
   * Handle scroll events to update sticky header and trigger pagination
   */
  onScroll(event: Event): void {
    const container = event.target as HTMLElement;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Check if we're near the bottom (300px threshold)
    // Only load if not already loading, not adjusting view, and has more data
    if (!this.isAdjustingView && !this.loadingMore() && !this.loading() && this.hasMore()) {
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      const now = Date.now();
      const timeSinceLastLoad = now - this.lastLoadTime;

      // Only trigger if within 300px AND at least 1000ms since last load (prevents rapid double-loading)
      if (distanceFromBottom < 300 && timeSinceLastLoad > 1000) {
        this.lastLoadTime = now;
        this.loadNextPage();
      }
    }

    // Update sticky header (existing logic)
    const dateHeaders = container.querySelectorAll<HTMLElement>('[data-header-id]');

    if (dateHeaders.length === 0) {
      this.currentStickyHeader.set(null);
      return;
    }

    const firstHeader = dateHeaders[0];
    const firstHeaderPositionInViewport = firstHeader.offsetTop - scrollTop;

    // Hide sticky header if:
    // 1. At the very top (scrollTop = 0), OR
    // 2. First header is visible (to avoid showing duplicate headers)
    if (scrollTop <= 0 || firstHeaderPositionInViewport >= 0) {
      this.currentStickyHeader.set(null);
      return;
    }

    let currentHeader: HTMLElement | null = null;

    // Read sticky header height so we account for it when determining when a group header has scrolled off
    const stickyEl = container.querySelector('.sticky-header-overlay');
    const stickyHeaderHeight = stickyEl ? (stickyEl as HTMLElement).offsetHeight : 0;

    // Find the current visible date section.
    // Only mark a header as "current" once its bottom edge has fully scrolled behind the sticky header,
    // preventing the double-label effect where both the sticky overlay and the group header are visible.
    dateHeaders.forEach((header) => {
      const headerTop = header.offsetTop;
      const headerHeight = (header as HTMLElement).offsetHeight;

      if (headerTop + headerHeight <= scrollTop + stickyHeaderHeight) {
        currentHeader = header;
      }
    });

    // Update the sticky header with current date
    if (currentHeader) {
      const headerElement = currentHeader as HTMLElement;
      const headerId = headerElement.getAttribute('data-header-id');
      if (headerId) {
        const allData = this.flatenedData();
        const header = allData.find(item => item.type === 'header' && item.id === headerId);
        if (header && header.count > 0) {
          const current = this.currentStickyHeader();
          if (!current || header.id !== current.id) {
            this.currentStickyHeader.set(header);
          }
        }
      }
    }
  }

  /**
   * Setup IntersectionObserver to detect when user scrolls to bottom
   */
  private setupIntersectionObserver(): void {
    // Clean up existing observer
    if (this.loadMoreObserver) {
      this.loadMoreObserver.disconnect();
    }

    // Get sentinel element from template
    const sentinel = this.loadMoreSentinel?.nativeElement;
    if (!sentinel) {
      return;
    }

    // Create observer
    this.loadMoreObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        // When sentinel is visible (user scrolled near bottom)
        // Don't load if we're adjusting the view (collapse/expand) or already loading
        if (entry.isIntersecting && !this.isAdjustingView && !this.loadingMore() && !this.loading() && this.hasMore()) {
          this.loadNextPage();
        }
      },
      {
        root: null, // viewport
        rootMargin: '300px', // Trigger 300px before reaching sentinel (earlier detection)
        threshold: 0.1 // Trigger when only 10% of sentinel is visible (more responsive)
      }
    );

    // Observe the sentinel
    this.loadMoreObserver.observe(sentinel);
  }

  /**
   * Load the initial page of data
   */
  private loadInitialPage(): void {
    // Guard: defer until IDB has finished loading entity stores into NgRx signals.
    // Without this guard the component fires on ngOnInit before loadIndexedDBToUI()
    // completes, reads empty stores, and sets hasMore=false — never retrying once
    // the real data arrives.
    if (!this.indexDbService.isIdbLoaded) {
      log.debug('loadInitialPage', `IDB not yet ready — deferring until idbLoaded$`);
      this.indexDbService.idbLoaded$.pipe(
        take(1),
        takeUntil(this.unSubscribe)
      ).subscribe(() => {
        log.debug('loadInitialPage', `idbLoaded$ fired — retrying initial load`);
        this.loadInitialPage();
      });
      return;
    }

    const mode = this.mode();
    const groupBy = this.groupBy();

    this.flatenedData.set([]);
    this.hasLoadedOnce.set(false);
    this.currentPage.set(1);
    this.futureIndex = null;  // Clear cached index so page 1 builds a fresh one

    log.debug('loadInitialPage', `Loading initial ${mode} page`, { groupBy, isFiltered: this.isFiltered() });

    // Check prefetch cache first (date grouping, no active filters)
    if (groupBy === 'date' && !this.isFiltered()) {
      log.debug('loadInitialPage', `Checking prefetch cache for ${mode}`);
      const cached = this.prefetchService.consume(mode);
      if (cached) {
        const showConfig = mode === 'PAST'
          ? this.viewSettings.pastShow()
          : this.viewSettings.futureShow();
        const currentShowInvalid = showConfig.includes('invalidEntry');

        log.debug('loadInitialPage', `Cache found for ${mode} — validating settings`, {
          cachedShowInvalid: cached.showInvalidEntries,
          currentShowInvalid,
          settingsMatch: cached.showInvalidEntries === currentShowInvalid,
          cachedItemCount: cached.items.length,
          cachedHasMore: cached.hasMore
        });

        if (cached.showInvalidEntries === currentShowInvalid) {
          // Cache matches current settings — instant render, no loader shown
          this.flatenedData.set(cached.items);
          this.cursor.set(cached.nextCursor);
          this.hasMore.set(cached.hasMore);
          this.currentPage.set(2);  // CRITICAL: prevents filter effect from re-loading
          this.hasLoadedOnce.set(true);
          // Restore the pre-built future index so subsequent pages reuse it without rebuilding
          if (mode === 'FUTURE' && cached.futureIndex) {
            this.futureIndex = cached.futureIndex;
          }
          log.debug('loadInitialPage', `Cache applied — flatenedData populated from prefetch`, {
            itemCount: this.flatenedData().length,
            hasMore: this.hasMore(),
            nextCursor: cached.nextCursor?.dateString ?? null,
            hasFutureIndex: this.futureIndex !== null
          });
          // loading stays false — no loader ever shown
          // Auto-load remaining pages in background without blocking UI
          if (cached.hasMore && cached.nextCursor) {
            log.debug('loadInitialPage', `Has more data — scheduling background loadNextPage`);
            setTimeout(() => this.loadNextPage(), 0);
          }
          return;
        } else {
          log.debug('loadInitialPage', `Cache discarded — settings mismatch (showInvalid: cached=${cached.showInvalidEntries}, current=${currentShowInvalid})`);
        }
      } else {
        log.debug('loadInitialPage', `No prefetch cache available for ${mode} — falling back to normal load`);
      }
    } else {
      log.debug('loadInitialPage', `Prefetch cache skipped`, { groupBy, isFiltered: this.isFiltered() });
    }

    // No cache / cache mismatch — normal loading with loader
    this.loading.set(true);
    this.hasMore.set(true);

    // For hashtag grouping, load all data at once
    if (groupBy === 'hashtag') {
      this.loadHashtagGroupedData();
    } else {
      // Date-based pagination
      this.cursor.set(this.paginationService.createInitialCursor(mode));
      this.loadNextPage();
    }
  }

  /**
   * Reset state and reload from the beginning
   */
  private resetAndReload(): void {
    // Prevent multiple simultaneous reloads
    if (this.isReloading()) {
      return;
    }

    // Only reload if we've already loaded initial data at least once
    if (!this.hasLoadedOnce()) {
      return;
    }

    const groupBy = this.groupBy();

    // Set reload lock
    this.isReloading.set(true);

    const mode = this.mode();

    // Update all state atomically to prevent race conditions
    this.flatenedData.set([]);
    this.loading.set(true);
    this.currentPage.set(1);
    this.futureIndex = null;  // Clear cached index so next FUTURE load builds a fresh one

    // Scroll to top
    const container = this.scrollContainer?.nativeElement;
    if (container) {
      container.scrollTop = 0;
    }

    // Use setTimeout to ensure all signals have settled before loading
    setTimeout(() => {
      // Choose loading method based on groupBy setting
      if (groupBy === 'hashtag') {
        this.loadHashtagGroupedData();
      } else {
        // Date-based pagination
        const newCursor = this.paginationService.createInitialCursor(mode);
        this.cursor.set(newCursor);
        this.hasMore.set(true);
        this.loadNextPage();
      }
      // Release lock after load starts
      this.isReloading.set(false);
    }, 0);
  }

  /**
   * Load the next page of data and append to existing items
   */
  loadNextPage(): void {
    const currentCursor = this.cursor();
    if (!currentCursor || !this.hasMore()) {
      log.debug('loadNextPage', 'Skipped — no cursor or hasMore=false', {
        hasCursor: !!currentCursor,
        hasMore: this.hasMore()
      });
      this.loading.set(false);
      this.loadingMore.set(false);
      return;
    }

    const pageNum = this.currentPage();

    // Set loading state
    if (pageNum === 1) {
      this.loading.set(true);
      this.loadPageData(currentCursor, pageNum);
    } else {
      // For subsequent pages, show loader first, then load data
      const loadStartTime = Date.now();
      this.loadingMore.set(true);

      // Manually trigger change detection to ensure loader renders immediately
      this.cdr.detectChanges();

      // Wait for next frame to ensure loader is rendered before loading data
      requestAnimationFrame(() => {
        this.loadPageData(currentCursor, pageNum, loadStartTime);
      });
    }
  }

  private loadPageData(currentCursor: DateCursor, pageNum: number, loadStartTime?: number): void {
    const filters = this.buildFilters();
    let shouldAutoLoadNext = false;

    log.info('loadNextPage', `Loading page ${pageNum}`, {
      cursor: currentCursor.dateString,
      direction: currentCursor.direction,
      currentItems: this.flatenedData().length
    });

    try {
      // FUTURE: use getPageSmart() with a pre-built index so empty date ranges are
      // skipped instantly (O(1) lookup) instead of scanning day-by-day.
      // The index is built once per load cycle and reused across pages.
      // PAST: keep getPage() — past data is typically dense and getPage() is fine there.
      let result;
      if (currentCursor.direction === 'future') {
        // Build index on first FUTURE page of this load cycle; reuse on subsequent pages
        if (!this.futureIndex) {
          const today = new Date();
          const futureLimit = new Date(today);
          futureLimit.setFullYear(today.getFullYear() + 10);
          const fromDate = currentCursor.dateString;
          const toDate = `${futureLimit.getFullYear()}-${String(futureLimit.getMonth() + 1).padStart(2, '0')}-${String(futureLimit.getDate()).padStart(2, '0')}`;

          this.futureIndex = this.indexService.buildIndex(filters, { from: fromDate, to: toDate });

        }
        result = this.paginationService.getPageSmart({
          cursor: currentCursor,
          pageSize: this.PAGE_SIZE,
          maxDatesToScan: this.MAX_DATES_TO_SCAN,
          filters
        }, this.futureIndex);
      } else {
        result = this.paginationService.getPage({
          cursor: currentCursor,
          pageSize: this.PAGE_SIZE,
          maxDatesToScan: this.MAX_DATES_TO_SCAN,
          filters
        });
      }

      // Get current data
      const currentItems = this.flatenedData();

      // Adjust group indices for new items to prevent conflicts
      const maxGroupIndex = currentItems.length > 0
        ? Math.max(...currentItems.filter(i => i.type === 'header').map(i => i.groupIndex)) + 1
        : 0;

      const adjustedItems = result.items.map(item => ({
        ...item,
        groupIndex: item.groupIndex + maxGroupIndex
      }));

      // Append new items to existing data (NO removal!)
      this.flatenedData.set([...currentItems, ...adjustedItems]);
      log.debug('loadPageData', `flatenedData updated`, {
        page: pageNum,
        newItems: adjustedItems.length,
        totalItems: this.flatenedData().length,
        totalEntityItems: this.flatenedData().filter(i => i.type === 'item').length
      });

      // Update pagination state
      this.cursor.set(result.nextCursor);
      this.hasMore.set(result.hasMore);
      this.currentPage.set(pageNum + 1);

      log.info('loadNextPage', `Page ${pageNum} loaded successfully`, {
        itemsLoaded: result.items.length,
        totalItems: this.flatenedData().length,
        hasMore: result.hasMore,
        nextPage: pageNum + 1
      });

      // Mark that we've completed at least one successful load
      this.hasLoadedOnce.set(true);

      // If no more data, prevent retry attempts by setting lastLoadTime far in future
      if (!result.hasMore) {
        this.lastLoadTime = Date.now() + 10000; // Prevent loading for next 10 seconds
      }

      // CRITICAL FIX: If we loaded 0 items but hasMore is still true,
      // automatically trigger next page to avoid infinite loader
      // (This happens when scanning dates that have no matching entities)
      shouldAutoLoadNext = result.items.length === 0 && result.hasMore && pageNum > 1;

    } catch (error) {
      log.error('loadNextPage', 'Error loading page', { error, page: pageNum });
    } finally {
      this.loading.set(false);

      // If no more data, hide loader immediately. Otherwise, ensure minimum 400ms display
      const shouldHideImmediately = !this.hasMore();

      if (shouldHideImmediately) {
        // No more data - hide loader immediately
        this.loadingMore.set(false);
      } else if (shouldAutoLoadNext) {
        // CRITICAL FIX: Page returned 0 items but hasMore=true
        // Hide loader and immediately trigger next page to continue loading
        this.loadingMore.set(false);

        // Use setTimeout(0) to allow UI to update before loading next page
        setTimeout(() => {
          this.loadNextPage();
        }, 0);
      } else if (loadStartTime) {
        // Has more data - ensure loader shows for minimum 400ms to prevent flicker
        const elapsedTime = Date.now() - loadStartTime;
        const minDisplayTime = 400;
        const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

        setTimeout(() => {
          this.loadingMore.set(false);

          // Re-setup observer after loading completes (ensures sentinel is observed)
          if (this.hasMore() && this.loadMoreSentinel?.nativeElement) {
            this.setupIntersectionObserver();
          }
        }, remainingTime);
      } else {
        this.loadingMore.set(false);

        // Re-setup observer after loading completes (ensures sentinel is observed)
        if (this.hasMore() && this.loadMoreSentinel?.nativeElement) {
          this.setupIntersectionObserver();
        }
      }
    }
  }

  /**
   * Build filters object from component state
   */
  private buildFilters(): PaginationFilters {
    const mode = this.mode();
    const showConfig = mode === 'PAST' ? this.viewSettings.pastShow() : this.viewSettings.futureShow();
    const showInvalidEntries = showConfig.includes('invalidEntry');

    return {
      query: this.filters.query(),
      features: this.filters.feature(),
      access: this.filters.role(),
      tags: this.filters.tags(),
      status: this.filters.status(),
      attachments: this.filters.attachments(),
      moods: this.filters.moods(),
      startDate: this.filters.startDate(),
      endDate: this.filters.endDate(),
      showInvalidEntries: showInvalidEntries,
      entityMetadataLookup: (entityType, setupId, dateString) => this.getEntityMetadata(entityType, setupId, dateString),
    };
  }

  /**
   * Load all past entities and group them by hashtag
   * Used when groupBy is 'hashtag' for Past screen
   */
  private loadHashtagGroupedData(): void {
    try {
      const mode = this.mode();
      const filters = this.buildFilters();
      const hashtags = this.ss.hashtags();
      const hashtagMap = new Map(hashtags.map(({ id, tag }) => [id, tag]));

      // Get all past entries using feature service (delegates to EntryService)
      let allEntries = this.featureService.getAllPastEntries(
        filters.query,
        filters.features,
        filters.tags,
        filters.startDate,
        filters.endDate,
        filters.showInvalidEntries
      );

      // Apply rich filters (status, attachments, moods, access) that getAllPastEntries doesn't handle
      const hasRichFilters = filters.status.length > 0 || filters.attachments.length > 0 ||
        filters.moods.length > 0 || filters.access.length > 0;
      if (hasRichFilters) {
        allEntries = allEntries.filter(entry => {
          const metadata = this.getEntityMetadata(entry.entityType, entry.setupId, entry.date);
          if (filters.status.length > 0 && !filters.status.includes(metadata.status)) return false;
          if (filters.attachments.length > 0) {
            const activeAttachments = Object.values(metadata.attachments).filter(a => !a.deletedAt);
            if (!filters.attachments.some(ft => activeAttachments.some(a => a.fileType === ft))) return false;
          }
          if (filters.moods.length > 0) {
            if (metadata.emotion === null || !filters.moods.includes(metadata.emotion)) return false;
          }
          if (filters.access.length > 0) {
            if (!metadata.role || !filters.access.includes(metadata.role)) return false;
          }
          return true;
        });
      }

      // Group entries by hashtag
      const groupedMap = new Map<string, { id: string; name: string; data: Entry[] }>();
      const noHashtagEntries: Entry[] = [];

      allEntries.forEach(entry => {
        if (!entry.tagIds || entry.tagIds.length === 0) {
          noHashtagEntries.push(entry);
        } else {
          // Filter only valid hashtags that exist in hashtagMap
          const validTags = entry.tagIds.filter(tagId => hashtagMap.has(tagId));

          if (validTags.length === 0) {
            noHashtagEntries.push(entry);
          } else {
            // Group by valid hashtags only
            validTags.forEach(tagId => {
              const id = tagId || '';
              if (!groupedMap.has(id)) {
                groupedMap.set(id, {
                  id,
                  name: `#${hashtagMap.get(id)}`,
                  data: []
                });
              }
              groupedMap.get(id)!.data.push(entry);
            });
          }
        }
      });

      // Convert to flattened data structure
      const items: FlatEntry[] = [];
      let groupIndex = 0;

      // Add hashtag groups
      groupedMap.forEach((group) => {
        // Sort entries within group by date (most recent first for past)
        const sortedEntries = [...group.data].sort((a, b) => {
          return mode === 'PAST' ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date);
        });

        // Add header
        items.push({
          type: 'header',
          groupIndex,
          itemIndex: -1,
          groupId: group.id,
          name: group.name,
          count: sortedEntries.length,
          id: group.id,
          dateString: '',
          entry: null,
          items: sortedEntries
        });

        // Add items
        sortedEntries.forEach((entry, itemIndex) => {
          items.push({
            type: 'item',
            groupIndex,
            itemIndex,
            groupId: group.id,
            name: entry.sortTitle,
            count: 1,
            id: entry.setupId,
            dateString: entry.date,
            entry,
            items: sortedEntries
          });
        });

        groupIndex++;
      });

      // Add "No Hashtags" group if there are entries without hashtags
      if (noHashtagEntries.length > 0) {
        const sortedNoHashtagEntries = [...noHashtagEntries].sort((a, b) => {
          return mode === 'PAST' ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date);
        });

        items.push({
          type: 'header',
          groupIndex,
          itemIndex: -1,
          groupId: 'no-hashtags',
          name: this.ss.t('common_groupByNoHashtags'),
          count: sortedNoHashtagEntries.length,
          id: 'no-hashtags',
          dateString: '',
          entry: null,
          items: sortedNoHashtagEntries
        });

        sortedNoHashtagEntries.forEach((entry, itemIndex) => {
          items.push({
            type: 'item',
            groupIndex,
            itemIndex,
            groupId: 'no-hashtags',
            name: entry.sortTitle,
            count: 1,
            id: entry.setupId,
            dateString: entry.date,
            entry,
            items: sortedNoHashtagEntries
          });
        });
      }

      this.flatenedData.set(items);
      this.hasMore.set(false); // No pagination for hashtag grouping
      this.hasLoadedOnce.set(true);

      log.debug('loadHashtagGroupedData', 'Hashtag grouped data loaded', {
        totalGroups: groupedMap.size + (noHashtagEntries.length > 0 ? 1 : 0),
        totalItems: allEntries.length
      });
    } catch (error) {
      log.error('loadHashtagGroupedData', 'Error loading hashtag grouped data', { error });
    } finally {
      this.loading.set(false);
      this.loadingMore.set(false);
    }
  }

  // Sticky header methods removed - using CSS position: sticky instead!

  /**
   * Toggle group expansion when header is clicked.
   * Mirrors Flutter's _onHeaderTap() behavior.
   */
  toggleGroupExpansion(groupId: string): void {
    const current = new Set(this.expandedGroups());

    if (current.has(groupId)) {
      current.delete(groupId);
    } else {
      current.add(groupId);
    }

    this.expandedGroups.set(current);
    log.debug('toggleGroupExpansion', `Toggled group ${groupId}`, { collapsed: current.has(groupId) });
  }

  /**
   * Check if a group is expanded.
   * When collapsedView setting is enabled, groups default to collapsed.
   * When collapsedView is disabled, groups default to expanded.
   * The expandedGroups set tracks groups that have been manually toggled from their default state.
   */
  isGroupExpanded(groupId: string): boolean {
    const isCollapsedViewEnabled = this.collapsedView();
    const hasBeenToggled = this.expandedGroups().has(groupId);

    // If collapsedView is enabled: default is collapsed, toggled means expanded
    // If collapsedView is disabled: default is expanded, toggled means collapsed
    if (isCollapsedViewEnabled) {
      // Default collapsed - if toggled, it's expanded
      return hasBeenToggled;
    } else {
      // Default expanded - if toggled, it's collapsed
      return !hasBeenToggled;
    }
  }

  /**
   * Check if any groups have been explicitly collapsed
   */
  hasCollapsedGroups(): boolean {
    return this.expandedGroups().size > 0;
  }

  // ============================================================================
  // HELPER METHODS (kept from original for compatibility)
  // ============================================================================

  getEntityData(entityName: EntityName, setupId: string, dateString: string): { attachments: Record<string, Attachment>; emotion: number | null; tags: string[], status: EntityStatus } {
    const emptyEntity: { attachments: Record<string, Attachment>; emotion: number | null; tags: string[], status: EntityStatus } = { attachments: {}, emotion: null, tags: [], status: 'none' };
    switch (entityName) {
      case 'todo':
        const todo = this.featureService.todoStore.idToTodo()[setupId] || null;
        return todo ? { attachments: todo.attachments, emotion: null, tags: todo.tags, status: this.todoService.getAttachmentStatus(todo, dateString) } : emptyEntity;
      case 'habit':
        const habit = (this.featureService.habitStore.setupToHabitMap()[setupId] && this.featureService.habitStore.setupToHabitMap()[setupId][dateString]) || null;
        return habit ? { attachments: habit.attachments, emotion: null, tags: habit.tags, status: this.habitService.getStatus(habit, dateString, true).status } : emptyEntity;
      case 'journal':
        const journal = (this.featureService.journalStore.setupToJournalMap()[setupId] && this.featureService.journalStore.setupToJournalMap()[setupId][dateString]) || null;
        return journal ? { attachments: journal.attachments, emotion: journal.emotion, tags: journal.tags, status: this.journalService.getStatus(journal, dateString, true) } : emptyEntity;
      case 'note':
        const note = this.noteStore.idToNote()[setupId] || null;
        return note ? { attachments: note.attachments, emotion: note.emotion ?? null, tags: note.tags, status: 'none' } : emptyEntity;
      case 'moneyTracker':
        const mtAction = this.moneyTrackerStore.idToTransaction()[setupId] || null;
        return mtAction ? { attachments: mtAction.attachments, emotion: null, tags: mtAction.tags, status: 'none' } : emptyEntity;
      case 'focus':
      case 'calendarIntegration':
      case 'list':
        return emptyEntity;
      default:
        log.warn('EntityPastAndFutureComponent.getEntityData', `Unknown entityName: ${entityName}`, { entityName });
        return emptyEntity;
    }
  };

  /**
   * Get entity metadata including access role for rich filtering.
   * Used as the entityMetadataLookup callback for PaginationFilters.
   */
  getEntityMetadata(entityType: EntityName, setupId: string, dateString: string): EntityMetadata {
    const data = this.getEntityData(entityType, setupId, dateString);
    const uid = this.cc.user?.uid;
    let role: CollaboraterRole | null = null;

    if (uid) {
      // Look up setup to determine access role from members
      let setupUid: string | undefined;
      let members: MembersModel | undefined;

      switch (entityType) {
        case 'todo': {
          const s = this.featureService.todoStore.idToTodo()[setupId];
          setupUid = s?.uid; members = s?.members;
          break;
        }
        case 'habit': {
          const s = this.featureService.habitStore.idToSetup()[setupId];
          setupUid = s?.uid; members = s?.members;
          break;
        }
        case 'journal': {
          const s = this.featureService.journalStore.idToSetup()[setupId];
          setupUid = s?.uid; members = s?.members;
          break;
        }
        case 'note': {
          const s = this.noteStore.idToNote()[setupId];
          setupUid = s?.uid; members = s?.members;
          break;
        }
        case 'moneyTracker': {
          const s = this.moneyTrackerStore.idToSetup()[setupId];
          setupUid = s?.uid; members = s?.members;
          break;
        }
        case 'focus': {
          const s = this.focusStore.setupsMap()[setupId];
          setupUid = s?.uid; members = s?.members;
          break;
        }
        case 'calendarIntegration': {
          const s = this.featureService.calendarStore.idToEventSetup()[setupId];
          setupUid = s?.uid; members = s?.members;
          break;
        }
      }

      if (setupUid) {
        role = setupUid === uid ? 'owner' : getRoleByUid(members, uid) || 'viewer';
      }
    }

    return {
      attachments: data.attachments,
      emotion: data.emotion,
      status: data.status,
      role,
    };
  }

  async toggleFilter() {
    if (!this.isFilter()) {
      this.isFilter.set(true);
      return;
    } else {
      const isFilterApplied = this.isFiltered();
      if (isFilterApplied) {
        const confirmed = await this.alertService.confirmRevamped({
          title: this.ss.t('filterApplied_title'),
          description: this.ss.t('filterApplied_content'),
          yesButtonText: this.ss.t('common_proceed'),
          noButtonText: this.ss.t('common_buttonCancel'),
          yesButtonColor: 'color-14',
          noButtonColor: 'color-7',
        });
        if (confirmed) {
          this.clearFilters();
          this.isFilter.set(false);
        } else {
          return;
        }
      } else {
        this.isFilter.set(false);
      }
    }
  }

  openView() {
    const dialogRef = this.modalService.openLevel3(EntityPastFutureViewComponent, {
      mode: this.mode()
    }, {
      autoFocus: false
    });
    return this.modalService.getClosedObservable(dialogRef);
  }

  trackByFlattened(index: number, item: FlatEntry): any {
    if (item.type === 'header') {
      return `header-${item.groupIndex}-${item.id}`;
    }
    const entry = item.entry;
    if (entry) {
      return `item-${entry.setupId}_${entry.date}_${entry.dayIndex}`;
    }
    return `item-${item.groupIndex}-${item.itemIndex}`;
  }

  toggleChildGroup(element: HTMLElement, groupId: string) {
    const isOpened = element.getAttribute('isOpen');
    const groupedElement = document.getElementById(groupId);
    if (!groupedElement) {
      return
    }
    if (isOpened === 'false') {
      groupedElement.setAttribute('isGroupOpened', 'true');
      element.setAttribute('isOpen', 'true');
    } else {
      groupedElement.setAttribute('isGroupOpened', 'false');
      element.setAttribute('isOpen', 'false');
    }
  }

  clearFilters() {
    this.filters.query.set('');
    this.filters.tags.set([]);
    this.filters.attachments.set([]);
    this.filters.moods.set([]);
    this.filters.status.set([]);
    this.filters.feature.set([]);
    this.filters.role.set([]);
    this.filters.startDate.set(null);
    this.filters.endDate.set(null);
    // Collapse the filter panel after clearing
    this.isFilter.set(false);
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent(this.mode() === 'PAST' ? 'past' : 'future', previousScreen);
        this.storageService.setCurrentScreen(this.mode() === 'PAST' ? 'past' : 'future');
      }
    });
  }

  // ============================================================================
  // REAL-TIME SYNC: Silent refresh when store data changes (e.g. mobile sync)
  // ============================================================================

  /**
   * Sets up an effect that watches all entity stores for data changes.
   * When the store updates (e.g. after a mobile sync), schedules a silent
   * refresh that patches flatenedData without any loader or scroll reset.
   */
  private setupStoreWatchEffect(): void {
    let initialized = false;

    effect(() => {
      // Track all entity store signals — Angular re-runs this effect only when data actually changes.
      // Habit/journal SETUPS must be tracked separately from entries:
      // a new recurring habit changes setups() but not setupToHabitMap() (no entries yet).
      const _todos = this.featureService.todoStore.todos();
      const _notes = this.noteStore.notesByDateString(); // notes grouped by createdAt date
      const _habitSetups = this.featureService.habitStore.setups();       // new/deleted recurring habits
      const _habitEntries = this.featureService.habitStore.setupToHabitMap(); // completions
      const _journalSetups = this.featureService.journalStore.setups();   // new/deleted journal setups
      const _journalEntries = this.featureService.journalStore.setupToJournalMap(); // entries
      const _moneySetups = this.featureService.moneyTrackerStore.setups(); // new/deleted trackers
      const _moneyTransactions = this.featureService.moneyTrackerStore.transactions();
      const _calendarEvents = this.featureService.calendarStore.events();

      untracked(() => {
        if (!initialized) {
          initialized = true;
          return;  // skip init run — initial data is loaded by ngOnInit
        }

        // Guards: skip if loading, reloading, nothing rendered yet, or mid-collapse
        if (!this.hasLoadedOnce() || this.loading() || this.isReloading()) return;
        if (this.isAdjustingView) return;
        if (this.groupBy() === 'hashtag') return;  // hashtag mode: filter effect handles reloads

        this.scheduleSilentRefresh();
      });
    });
  }

  /**
   * Debounces the silent refresh — stores can emit multiple times during a bulk sync.
   * Waits 300ms after the last emission before running the refresh.
   */
  private scheduleSilentRefresh(): void {
    if (this.silentRefreshTimer) {
      clearTimeout(this.silentRefreshTimer);
    }
    this.silentRefreshTimer = setTimeout(() => {
      this.silentRefreshTimer = null;
      this.performSilentRefresh();
    }, 300);
  }

  /**
   * Re-queries all currently rendered dates from the store and patches flatenedData.
   * No loader shown, no scroll reset — completely invisible to the user unless data changed.
   *
   * Handles: entity added/updated/deleted on a date already in view.
   * Does NOT handle: entity added on a date not yet loaded (appears naturally on scroll).
   */
  private performSilentRefresh(): void {
    if (!this.hasLoadedOnce() || this.loading() || this.isReloading()) return;

    const currentData = this.flatenedData();
    if (!currentData.length) return;

    const filters = this.buildFilters();

    // Collect all unique dates currently rendered, preserving their order
    const renderedDates: string[] = [];
    const seenDates = new Set<string>();
    currentData.forEach(item => {
      if (item.dateString && !seenDates.has(item.dateString)) {
        seenDates.add(item.dateString);
        renderedDates.push(item.dateString);
      }
    });

    if (!renderedDates.length) return;

    // Rebuild flatenedData for all rendered dates with fresh store data
    const newData: FlatEntry[] = [];
    let groupIndex = 0;
    let hasChanges = false;

    renderedDates.forEach(dateString => {
      const freshItems = this.paginationService.buildDateGroupItems(dateString, filters, groupIndex);

      // Compare item IDs in order to detect changes (add/delete/reorder)
      const currentItems = currentData.filter(d => d.type === 'item' && d.dateString === dateString);
      const freshItemEntries = freshItems.filter(d => d.type === 'item');

      if (freshItemEntries.length !== currentItems.length) {
        hasChanges = true;
      } else if (freshItemEntries.some((item, i) => item.id !== currentItems[i].id)) {
        hasChanges = true;
      }

      if (freshItems.length > 0) {
        newData.push(...freshItems);
        groupIndex++;
      } else if (currentItems.length > 0) {
        // Date had entities before but is now empty (e.g. entity deleted) — skip it
        hasChanges = true;
      }
    });

    if (hasChanges) {
      this.flatenedData.set(newData);
      log.info('performSilentRefresh', 'Silently patched flatenedData from store change', {
        datesRefreshed: renderedDates.length,
        newItemCount: newData.filter(d => d.type === 'item').length
      });
    }
  }

  ngOnDestroy() {
    this.as.screenExitEvent(this.mode() === 'PAST' ? 'past' : 'future', this.screenEnterTime());
    this.unSubscribe.next();
    this.unSubscribe.complete();
    this.loading.set(false);

    // Invalidate prefetch cache so next visit rebuilds with fresh store data
    this.prefetchService.invalidate();

    // Clean up IntersectionObserver
    if (this.loadMoreObserver) {
      this.loadMoreObserver.disconnect();
      this.loadMoreObserver = undefined;
    }

    // Clean up silent refresh timer
    if (this.silentRefreshTimer) {
      clearTimeout(this.silentRefreshTimer);
      this.silentRefreshTimer = null;
    }

    log.debug('ngOnDestroy', 'Destroyed Past/Future component');

    // Re-prefetch both modes so the next visit to either screen is instant.
    // setTimeout(0) defers to the next macro-task so it doesn't block the
    // current navigation frame. The 1000ms buffer is no longer needed because
    // CloudSyncService.saveData() calls prefetchService.invalidate() AFTER the
    // IDB write completes, so any created entities are already in IDB by this point.
    setTimeout(() => {
      this.prefetchService.prefetch('PAST');
      this.prefetchService.prefetch('FUTURE');
    }, 0);
  }
}
